// src/components/testout/TestOutDocumentationForm.jsx

import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button, Input } from '../ui';

export function TestOutDocumentationForm({
  exercise,
  existingData,
  injurySide,
  onSave,
  onClose,
}) {
  const [formData, setFormData] = useState({
    level: existingData?.level || null,
    reps: existingData?.reps || '',
    leftReps: existingData?.leftReps || '',
    rightReps: existingData?.rightReps || '',
    leftWeight: existingData?.leftWeight || '',
    rightWeight: existingData?.rightWeight || '',
    leftDistance: existingData?.leftDistance || '',
    rightDistance: existingData?.rightDistance || '',
    leftHoldTime: existingData?.leftHoldTime || '',
    rightHoldTime: existingData?.rightHoldTime || '',
    leftHeld30: existingData?.leftHeld30 ?? null,
    rightHeld30: existingData?.rightHeld30 ?? null,
    notes: existingData?.notes || '',
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Build clean data object with only relevant fields
    const cleanData = {};

    if (exercise.hasLevel && formData.level) {
      cleanData.level = formData.level;
    }

    if (exercise.isMachine && exercise.hasReps) {
      // Machine exercises: single reps, separate weights
      cleanData.reps = parseInt(formData.reps) || 0;
      if (exercise.hasWeight) {
        cleanData.leftWeight = parseFloat(formData.leftWeight) || 0;
        cleanData.rightWeight = parseFloat(formData.rightWeight) || 0;
      }
    } else if (exercise.hasReps) {
      // Non-machine: separate reps per side
      cleanData.leftReps = parseInt(formData.leftReps) || 0;
      cleanData.rightReps = parseInt(formData.rightReps) || 0;
      if (exercise.hasWeight) {
        cleanData.leftWeight = parseFloat(formData.leftWeight) || 0;
        cleanData.rightWeight = parseFloat(formData.rightWeight) || 0;
      }
    }

    if (exercise.hasDistance) {
      cleanData.leftDistance = parseFloat(formData.leftDistance) || 0;
      cleanData.rightDistance = parseFloat(formData.rightDistance) || 0;
    }

    if (exercise.hasHoldTime) {
      if (exercise.isSidePlank) {
        // Side plank: use the held30 boolean to set hold time
        cleanData.leftHoldTime = formData.leftHeld30 ? 30 : (parseInt(formData.leftHoldTime) || 0);
        cleanData.rightHoldTime = formData.rightHeld30 ? 30 : (parseInt(formData.rightHoldTime) || 0);
        cleanData.leftHeld30 = formData.leftHeld30;
        cleanData.rightHeld30 = formData.rightHeld30;
      } else {
        cleanData.leftHoldTime = parseInt(formData.leftHoldTime) || 0;
        cleanData.rightHoldTime = parseInt(formData.rightHoldTime) || 0;
      }
    }

    if (formData.notes.trim()) {
      cleanData.notes = formData.notes.trim();
    }

    onSave(cleanData);
  };

  const injuredLabel = injurySide === 'left' ? 'Left (Injured)' : 'Right (Injured)';
  const uninjuredLabel = injurySide === 'left' ? 'Right (Uninjured)' : 'Left (Uninjured)';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-end justify-center sm:items-center p-0 sm:p-4">
        <div className="relative bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900">
              Document: {exercise.displayName}
            </h2>
            <button
              onClick={onClose}
              className="p-2 -mr-2 rounded-full hover:bg-gray-100"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Level Selection */}
            {exercise.hasLevel && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Level
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[1, 2, 3].map(level => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => updateField('level', level)}
                      className={`
                        py-3 px-4 rounded-lg border-2 text-center font-medium transition-all
                        ${formData.level === level
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }
                      `}
                    >
                      Level {level}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Side Plank - Special Yes/No Questions */}
            {exercise.isSidePlank && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Were you able to hold for 30 seconds on your {injurySide} side?
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => updateField(injurySide === 'left' ? 'leftHeld30' : 'rightHeld30', true)}
                      className={`
                        py-3 px-4 rounded-lg border-2 text-center font-medium transition-all
                        ${(injurySide === 'left' ? formData.leftHeld30 : formData.rightHeld30) === true
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }
                      `}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => updateField(injurySide === 'left' ? 'leftHeld30' : 'rightHeld30', false)}
                      className={`
                        py-3 px-4 rounded-lg border-2 text-center font-medium transition-all
                        ${(injurySide === 'left' ? formData.leftHeld30 : formData.rightHeld30) === false
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }
                      `}
                    >
                      No
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Were you able to hold for 30 seconds on your {injurySide === 'left' ? 'right' : 'left'} side?
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => updateField(injurySide === 'left' ? 'rightHeld30' : 'leftHeld30', true)}
                      className={`
                        py-3 px-4 rounded-lg border-2 text-center font-medium transition-all
                        ${(injurySide === 'left' ? formData.rightHeld30 : formData.leftHeld30) === true
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }
                      `}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => updateField(injurySide === 'left' ? 'rightHeld30' : 'leftHeld30', false)}
                      className={`
                        py-3 px-4 rounded-lg border-2 text-center font-medium transition-all
                        ${(injurySide === 'left' ? formData.rightHeld30 : formData.leftHeld30) === false
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }
                      `}
                    >
                      No
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Machine Exercises - Single Reps, Separate Weights */}
            {exercise.isMachine && exercise.hasReps && !exercise.isSidePlank && (
              <div className="space-y-4">
                <Input
                  label="Reps (same for both sides)"
                  type="number"
                  value={formData.reps}
                  onChange={(e) => updateField('reps', e.target.value)}
                  placeholder="Enter reps"
                  min="0"
                />

                {exercise.hasWeight && (
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label={`${injuredLabel} Weight (lbs)`}
                      type="number"
                      step="0.5"
                      value={injurySide === 'left' ? formData.leftWeight : formData.rightWeight}
                      onChange={(e) => updateField(injurySide === 'left' ? 'leftWeight' : 'rightWeight', e.target.value)}
                      placeholder="0"
                      min="0"
                    />
                    <Input
                      label={`${uninjuredLabel} Weight (lbs)`}
                      type="number"
                      step="0.5"
                      value={injurySide === 'left' ? formData.rightWeight : formData.leftWeight}
                      onChange={(e) => updateField(injurySide === 'left' ? 'rightWeight' : 'leftWeight', e.target.value)}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Non-Machine Exercises - Separate Reps/Weights per Side */}
            {!exercise.isMachine && exercise.hasReps && !exercise.isSidePlank && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label={`${injuredLabel} Reps`}
                    type="number"
                    value={injurySide === 'left' ? formData.leftReps : formData.rightReps}
                    onChange={(e) => updateField(injurySide === 'left' ? 'leftReps' : 'rightReps', e.target.value)}
                    placeholder="0"
                    min="0"
                  />
                  <Input
                    label={`${uninjuredLabel} Reps`}
                    type="number"
                    value={injurySide === 'left' ? formData.rightReps : formData.leftReps}
                    onChange={(e) => updateField(injurySide === 'left' ? 'rightReps' : 'leftReps', e.target.value)}
                    placeholder="0"
                    min="0"
                  />
                </div>

                {exercise.hasWeight && (
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label={`${injuredLabel} Weight (lbs)`}
                      type="number"
                      step="0.5"
                      value={injurySide === 'left' ? formData.leftWeight : formData.rightWeight}
                      onChange={(e) => updateField(injurySide === 'left' ? 'leftWeight' : 'rightWeight', e.target.value)}
                      placeholder="0"
                      min="0"
                    />
                    <Input
                      label={`${uninjuredLabel} Weight (lbs)`}
                      type="number"
                      step="0.5"
                      value={injurySide === 'left' ? formData.rightWeight : formData.leftWeight}
                      onChange={(e) => updateField(injurySide === 'left' ? 'rightWeight' : 'leftWeight', e.target.value)}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Distance (Hop Tests) */}
            {exercise.hasDistance && (
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label={`${injuredLabel} Distance (in)`}
                  type="number"
                  step="0.5"
                  value={injurySide === 'left' ? formData.leftDistance : formData.rightDistance}
                  onChange={(e) => updateField(injurySide === 'left' ? 'leftDistance' : 'rightDistance', e.target.value)}
                  placeholder="0"
                  min="0"
                />
                <Input
                  label={`${uninjuredLabel} Distance (in)`}
                  type="number"
                  step="0.5"
                  value={injurySide === 'left' ? formData.rightDistance : formData.leftDistance}
                  onChange={(e) => updateField(injurySide === 'left' ? 'rightDistance' : 'leftDistance', e.target.value)}
                  placeholder="0"
                  min="0"
                />
              </div>
            )}

            {/* Notes */}
            {exercise.hasNotes && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  placeholder="Any additional notes..."
                  rows={3}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary placeholder:text-gray-400 resize-none"
                />
              </div>
            )}
          </form>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 flex gap-3 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit}
              fullWidth
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}